import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { IconX, IconPackage } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useBulkUpdateStatusMutation } from "../../../apis/operationApis";
import { useGeneratePicklistMutation } from "../../../apis/picklistApis";
import { useFetchStaffsAllData } from "../../../apis/staffApis";
import { useLanguage } from "../../../context/LanguageContext";

interface BulkStatusUpdateModalProps {
  onClose: () => void;
  fetchData: () => void;
  targetStatus: string; // "ORDER_CREATED" | "PICKED" | "PACKED"
}

const BulkStatusUpdateModal: React.FC<BulkStatusUpdateModalProps> = ({
  onClose,
  fetchData,
  targetStatus,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [textareaValue, setTextareaValue] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [updateToPicked, setUpdateToPicked] = useState(false);
  const [picklistType, setPicklistType] = useState<
    "SINGLE_ORDER" | "BATCH_ORDER"
  >("BATCH_ORDER");

  const { mutate: bulkUpdate, isPending: isUpdating } =
    useBulkUpdateStatusMutation();
  const generatePicklistMutation = useGeneratePicklistMutation();
  const { data: staffData } = useFetchStaffsAllData();

  const isPending = isUpdating || generatePicklistMutation.isPending;

  // Helper to parse unique IDs from textarea
  const getUniqueIdentifiers = (value: string) => {
    return Array.from(
      new Set(
        value
          .split(/[\n,\t\s]+/)
          .map((o) => o.trim())
          .filter((o) => o.length > 0),
      ),
    );
  };

  const selectedOrders = getUniqueIdentifiers(textareaValue);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextareaValue(e.target.value);
  };

  const handleSubmit = () => {
    if (selectedOrders.length === 0) {
      toast.error(t("please enter at least one identifier"));
      return;
    }

    bulkUpdate(
      {
        identifiers: selectedOrders,
        targetStatus,
        type: targetStatus === "PICKLIST_GENERATED" ? picklistType : undefined,
        assignedTo:
          targetStatus === "PICKLIST_GENERATED"
            ? assignedTo || undefined
            : undefined,
        updateToPicked:
          targetStatus === "PICKLIST_GENERATED" ? updateToPicked : undefined,
      },
      {
        onSuccess: (res: any) => {
          if (res.success) {
            toast.success(res.message || t("bulk update successful"));
            if (targetStatus === "PICKLIST_GENERATED") {
              queryClient.invalidateQueries({ queryKey: ["picklists"] });
            }
            fetchData();
            onClose();
          } else {
            toast.error(res.message || t("failed to process orders"));
          }
        },
        onError: (error: any) => {
          toast.error(error?.message || t("an error occurred during bulk update"));
        },
      },
    );
  };

  const isNewLayout = targetStatus === "ORDER_CREATED" || targetStatus === "PICKLIST_GENERATED";

  /** Shared inner content used in both layout variants */
  const renderContent = () => (
    <div className="w-full space-y-4">
      <div className="flex flex-col gap-1 px-2">
        <p className="text-xs text-gray-500 font-medium">
          {t("paste awb numbers or reference ids below to transition them to")}{" "}
          <b className="text-[#212121]">{t(targetStatus)}</b>{" "}
          {t("status. only orders in the previous logical status will be updated.")}
        </p>
      </div>

      {targetStatus === "PICKLIST_GENERATED" && (
        <div className="flex flex-col gap-4 bg-white p-4 rounded-2xl border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("picklist type")}
              </label>
              <select
                value={picklistType}
                onChange={(e) =>
                  setPicklistType(e.target.value as any)
                }
                className="w-full bg-slate-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FBD037]/30 transition-all"
              >
                <option value="BATCH_ORDER">{t("batch order")}</option>
                <option value="SINGLE_ORDER">{t("single order")}</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("assign staff (optional)")}
              </label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full bg-slate-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FBD037]/30 transition-all"
              >
                <option value="">{t("select a staff member...")}</option>
                {Array.isArray(staffData) &&
                  staffData.map((staff: any) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.fullName || staff.email}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-2">
            <label className="relative flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={updateToPicked}
                onChange={(e) =>
                  setUpdateToPicked(e.target.checked)
                }
                className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-slate-50 checked:bg-[#FBD037] checked:text-white transition-all"
              />
              <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                ✓
              </span>
            </label>
            <span
              className="text-sm font-medium text-gray-700 cursor-pointer select-none"
              onClick={() => setUpdateToPicked(!updateToPicked)}
            >
              {t("instantly update picklist to completed and orders to picked")}
            </span>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#D2D2D2] rounded-2xl p-4 h-[220px] focus-within:ring-2 focus-within:ring-[#FBD037]/30 transition-all">
        <textarea
          value={textareaValue}
          onChange={handleTextareaChange}
          placeholder={t("paste identifiers here (e.g. tr1001, obp1000000049)...")}
          className="w-full h-full resize-none outline-none bg-transparent text-sm text-[#212121] placeholder:text-gray-400 font-mono scrollbar-clean"
        />
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500 bg-slate-100 px-4 py-2 rounded-full border border-slate-200">
          {selectedOrders.length > 0 ? (
            <span>
              {t("detected")}{" "}
              <b className="text-[#212121]">
                {selectedOrders.length}
              </b>{" "}
              {t("unique identifiers")}
            </span>
          ) : (
            <span>{t("waiting for input...")}</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop overlay for mobile & tablet screen sizes */}
      <div
        className="fixed inset-0 bg-black/40 z-40 lg:hidden"
        onClick={onClose}
      />

      <div
        className={`fixed top-20 bottom-0 right-0 z-50 w-full sm:max-w-2xl bg-white/20  animate-slide-left flex flex-col   rounded-l-3xl lg:rounded-3xl border border-gray-200  overflow-hidden   ${
          isNewLayout
            ? "lg:sticky lg:top-[90px] lg:self-start lg:col-span-5 xl:col-span-4 lg:h-[calc(100vh-120px)] lg:z-30"
            : "lg:static lg:col-span-5 xl:col-span-4 lg:top-auto lg:bottom-auto lg:h-auto"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
          {isNewLayout ? (
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#FBD037]/20 flex items-center justify-center text-[#212121] shrink-0">
                <IconPackage size={24} stroke={1.5} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#212121]">{t("bulk status update")}</h2>
              </div>
            </div>
          ) : (
            <h1 className="text-xl font-bold text-[#212121]">{t("bulk status update")}</h1>
          )}

          <div className="flex items-center gap-3">
            {isNewLayout && (
              <button
                onClick={handleSubmit}
                disabled={isPending || selectedOrders.length === 0}
                className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] border border-[#FBD037] flex items-center rounded-lg py-2 px-5 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {isPending ? t("processing...") : t("submit")}
              </button>
            )}
            
            <button
              onClick={onClose}
              className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 shrink-0"
              title={t("close")}
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        {isNewLayout ? (
          <div className="w-full py-4 px-6 bg-slate-50/50 flex-1 overflow-y-auto min-h-0">
            {renderContent()}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/50 space-y-4">
            <div className="rounded-3xl border border-gray-200 bg-white shadow-sm p-4 sm:p-6">
              {renderContent()}
            </div>
          </div>
        )}

        {/* Footer */}
        {!isNewLayout && (
          <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-white flex-shrink-0">
            <button
              className="w-full sm:w-auto h-11 px-6 rounded-full border border-[#D8D8D8] bg-white hover:bg-gray-50 transition-all duration-200 font-semibold cursor-pointer text-slate-700 text-sm"
              onClick={onClose}
            >
              {t("cancel")}
            </button>

            <button
              onClick={handleSubmit}
              disabled={isPending || selectedOrders.length === 0}
              className={`${
                isPending || selectedOrders.length === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                  : "bg-[#FBD037] hover:bg-yellow-400 text-[#212121] border border-[#FBD037] shadow-sm active:scale-95 cursor-pointer"
              } flex items-center rounded-full px-8 py-2.5 space-x-2 transition-all text-sm font-semibold h-11`}
            >
              {isPending && (
                <svg
                  className="animate-spin h-5 w-5 text-[#212121]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16z"
                  />
                </svg>
              )}
              <span>{isPending ? t("processing...") : t("submit")}</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default BulkStatusUpdateModal;
